import { Controller } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { PRESS_ENTER_TO_ADD_OPTION } from "../../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../../utils/errors";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import Chip from "@mui/material/Chip";

const filter = createFilterOptions();

const INSTAGRAM_USERNAME_REGEX = /^[a-zA-Z0-9._]+$/;

const normalizeUsername = (value) =>
  value ? value.trim().toLowerCase().replace(/^@/, "") : "";

const validateInstagramUsername = (value) => {
  const normalized = normalizeUsername(value);
  return normalized.length > 0 && INSTAGRAM_USERNAME_REGEX.test(normalized);
};

const transformInstagramUsername = (value) =>
  value.trim().toLowerCase().replace(/^@/, "");

const InstagramCollaboratorsField = ({
  control,
  errors = {},
  name,
  id = name,
  label = "",
  placeholder = "",
  helperText = "",
  fullWidth = true,
  disabled = false,
  variant = "filled",
  sx,
  className = "",
  color = "primary",
  InputProps = {},
  InputLabelProps = {},
  componentsProps = {},
}) => {
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  return (
    <Controller
      control={control}
      name={name}
      id={id}
      rules={{}}
      defaultValue={[]}
      render={({ field }) => (
        <Autocomplete
          label={label}
          hasLabel={Boolean(label)}
          hiddenLabel={!label}
          {...field}
          multiple
          id={id}
          limitTags={3}
          value={field.value || []}
          disabled={disabled}
          onChange={(_, newValue) => {
            let processedValues = newValue.map((item) => {
              let value = "";
              if (typeof item === "string") {
                value = item;
              } else if (item.inputValue) {
                value = item.inputValue;
              } else {
                value = item;
              }
              return transformInstagramUsername(value);
            });

            processedValues = processedValues.filter((val) =>
              validateInstagramUsername(val)
            );
            processedValues = [...new Set(processedValues)];
            processedValues = processedValues.slice(0, 3);

            field.onChange(processedValues);
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;

            if (inputValue !== "") {
              filtered.push({
                inputValue,
                label: t(PRESS_ENTER_TO_ADD_OPTION),
              });
            }

            return filtered;
          }}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            if (option.inputValue) {
              return option.inputValue;
            }
            return option;
          }}
          renderOption={(props, option) => (
            <li {...props}>{option.label || option}</li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={typeof option === "string" ? option : option.inputValue}
                {...getTagProps({ index })}
                sx={{
                  backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                }}
              />
            ))
          }
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={[]}
          freeSolo
          componentsProps={componentsProps}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              hiddenLabel={!label}
              fullWidth={fullWidth}
              placeholder={placeholder}
              error={!!error}
              helperText={error?.message || helperText}
              variant={variant}
              color={color}
              className={className}
              InputProps={{
                ...params.InputProps,
                ...InputProps,
              }}
              InputLabelProps={InputLabelProps}
              sx={{
                ...sx,
                backgroundColor: "fields.background",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                ".MuiFormHelperText-root": {
                  mx: 0,
                  position: "absolute",
                  bottom: "-20px",
                  left: 0,
                },
                ".MuiOutlinedInput-root": {
                  marginBottom: "20px",
                },
              }}
            />
          )}
        />
      )}
    />
  );
};

export default InstagramCollaboratorsField;
